import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowDownTrayIcon,
  BanknotesIcon,
  CheckCircleIcon,
  DocumentArrowUpIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  Modal,
  ModalFooter,
  Select,
} from '@/components/common';
import { getErrorMessage } from '@/services';
import { formatDateTime } from '@/utils';
import {
  PAYMENT_METHODS,
  PAYMENT_OUTCOMES,
  SERVICING_DOCUMENT_TYPES,
  UPLOAD_ACCEPT_ATTRIBUTE,
  formatFileSize,
  paymentBadgeVariant,
  paymentStatusLabel,
  serviceRequestWorkflowApi,
  servicingDocumentLabel,
  validateServicingFile,
  type RequiredDocument,
  type ServicingDocument,
  type ServicingPayment,
} from './serviceRequestWorkflow';

/**
 * Board #223 — the Pending Documents and Pending Payment cards.
 *
 * Two rules run through every control here, and both are enforced server-side
 * as well (`ServiceRequestViewSet.BROKER_ADMIN_ACTIONS`,
 * `ServiceRequestDocumentViewSet.BROKER_ADMIN_ACTIONS`,
 * `service_request_workflow.blocking_reason`):
 *
 * 1. The customer supplies — documents and the payment they made. They never
 *    approve. `canApprove` below is Broker-Admin-only; hiding the button is the
 *    courtesy, the 403 is the gate.
 * 2. Nothing renders a state this screen invented. The checklist and the
 *    payment record come from the detail payload; when the API does not report
 *    them (a rolling deploy where the SPA is ahead of gunicorn) the cards say
 *    so instead of drawing an empty checklist that reads as "nothing required".
 *
 * W3h: `request_payment` now runs the documents gate too -- billing is
 * refused while a requested document is still outstanding. `ServicingPaymentCard`
 * disables "Request Payment" with a reason in that case (a courtesy; the API
 * refusal is the real gate and still surfaces as a toast on failure).
 */

interface WorkflowCardProps {
  requestId: string;
  requestStatus: string;
  /** Broker Admin: the only role that may approve documents or verify a payment. */
  canApprove: boolean;
  /** May attach documents — the customer and the broker side. */
  canUpload: boolean;
  /** Refetch the request after any write; the payload carries all the state. */
  onChanged: () => void;
}

const CLOSED_STATUSES = ['completed', 'rejected', 'cancelled'];

function MissingFromApi({ what }: { what: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
      <ExclamationTriangleIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
      <p className="text-sm text-gray-600 dark:text-gray-300">
        This server build does not report {what}, so it cannot be shown here. Reload after the next
        deployment.
      </p>
    </div>
  );
}

function ChecklistRow({ entry }: { entry: RequiredDocument }) {
  const tone =
    entry.status === 'approved'
      ? { Icon: CheckCircleIcon, className: 'text-green-500', text: 'Approved' }
      : entry.status === 'rejected'
        ? { Icon: XCircleIcon, className: 'text-red-500', text: 'Rejected — send a replacement' }
        : entry.status === 'uploaded'
          ? { Icon: DocumentTextIcon, className: 'text-amber-500', text: 'Uploaded, awaiting review' }
          : { Icon: ExclamationTriangleIcon, className: 'text-gray-400', text: 'Not uploaded yet' };

  return (
    <li className="flex items-start gap-3 py-2">
      <tone.Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tone.className}`} />
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white">{entry.label}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {tone.text}
          {entry.reason ? ` — ${entry.reason}` : ''}
        </p>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Pending Documents
// ---------------------------------------------------------------------------

interface DocumentsCardProps extends WorkflowCardProps {
  documents: ServicingDocument[];
  /** `null` means the API did not report a checklist — see MissingFromApi. */
  checklist: RequiredDocument[] | null;
}

export function ServicingDocumentsCard({
  requestId,
  requestStatus,
  canApprove,
  canUpload,
  onChanged,
  documents,
  checklist,
}: DocumentsCardProps) {
  const closed = CLOSED_STATUSES.includes(requestStatus);
  const uploadAllowed = canUpload && !closed;
  const outstanding = (checklist ?? []).filter((entry) => entry.status !== 'approved');

  const [showUpload, setShowUpload] = useState(false);
  const [documentType, setDocumentType] = useState('');
  const [customName, setCustomName] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [rejecting, setRejecting] = useState<ServicingDocument | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const closeUpload = () => {
    setShowUpload(false);
    setDocumentType('');
    setCustomName('');
    setFiles([]);
    setFileErrors([]);
  };

  const openUploadFor = (code?: string) => {
    setDocumentType(code ?? '');
    setCustomName('');
    setFiles([]);
    setFileErrors([]);
    setShowUpload(true);
  };

  const handleFilesChosen = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = Array.from(event.target.files ?? []);
    const accepted: File[] = [];
    const rejected: string[] = [];
    for (const file of chosen) {
      const problem = validateServicingFile(file);
      if (problem) rejected.push(problem);
      else accepted.push(file);
    }
    setFiles(accepted);
    setFileErrors(rejected);
  };

  /** Upload each file in turn and report exactly what landed and what did not. */
  const uploadMutation = useMutation({
    mutationFn: async () => {
      const uploaded: string[] = [];
      const failures: string[] = [];
      for (const file of files) {
        try {
          const saved = await serviceRequestWorkflowApi.uploadDocument(requestId, {
            file,
            documentType,
            name: files.length === 1 ? customName : '',
          });
          uploaded.push(saved.name);
        } catch (err) {
          failures.push(`${file.name}: ${getErrorMessage(err)}`);
        }
      }
      return { uploaded, failures };
    },
    onSuccess: ({ uploaded, failures }) => {
      onChanged();
      if (uploaded.length > 0) {
        toast.success(
          uploaded.length === 1 ? `${uploaded[0]} uploaded` : `${uploaded.length} documents uploaded`
        );
      }
      failures.forEach((message) => toast.error(message));
      if (failures.length === 0) closeUpload();
      else setFiles([]);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const reviewMutation = useMutation({
    mutationFn: ({
      document,
      decision,
      reason,
    }: {
      document: ServicingDocument;
      decision: 'approved' | 'rejected';
      reason: string;
    }) => serviceRequestWorkflowApi.reviewDocument(requestId, document.id, decision, reason),
    onSuccess: (_data, variables) => {
      onChanged();
      setRejecting(null);
      setRejectReason('');
      toast.success(
        variables.decision === 'approved'
          ? `${variables.document.name} approved`
          : `${variables.document.name} sent back to the customer`
      );
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (document: ServicingDocument) =>
      serviceRequestWorkflowApi.deleteDocument(requestId, document.id),
    onSuccess: () => {
      onChanged();
      toast.success('Document deleted');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const downloadMutation = useMutation({
    mutationFn: (document: ServicingDocument) =>
      serviceRequestWorkflowApi.downloadDocument(requestId, document),
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const requestDocumentsMutation = useMutation({
    mutationFn: () => serviceRequestWorkflowApi.requestDocuments(requestId, {}),
    onSuccess: () => {
      onChanged();
      toast.success('The customer has been asked for the required documents.');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const approveDocumentsMutation = useMutation({
    mutationFn: (nextStatus: string) =>
      serviceRequestWorkflowApi.approveDocuments(requestId, { next_status: nextStatus }),
    onSuccess: () => {
      onChanged();
      toast.success('Documents approved. The request has moved on.');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  return (
    <>
      <Card>
        <CardHeader
          title="Required Documents"
          subtitle={
            checklist === null
              ? undefined
              : checklist.length === 0
                ? 'This request type needs no documents'
                : outstanding.length === 0
                  ? 'Every required document has been approved'
                  : `${outstanding.length} of ${checklist.length} still outstanding`
          }
          action={
            uploadAllowed && checklist !== null && checklist.length > 0 ? (
              <Button
                size="sm"
                onClick={() => openUploadFor()}
                leftIcon={<DocumentArrowUpIcon className="w-4 h-4" />}
              >
                Upload Document
              </Button>
            ) : undefined
          }
        />

        {checklist === null ? (
          <MissingFromApi what="the document checklist" />
        ) : checklist.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No documents are required for this request type.
          </p>
        ) : (
          <>
            {requestStatus === 'pending_documents' && outstanding.length > 0 && (
              <div className="mb-3 flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  This request stays at Pending Documents until a Broker Admin has approved every
                  item below.
                </p>
              </div>
            )}
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {checklist.map((entry) => (
                <ChecklistRow key={entry.code} entry={entry} />
              ))}
            </ul>
            {uploadAllowed && outstanding.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {outstanding
                  .filter((entry) => entry.status !== 'uploaded')
                  .map((entry) => (
                    <Button
                      key={entry.code}
                      variant="outline"
                      size="sm"
                      onClick={() => openUploadFor(entry.code)}
                      leftIcon={<DocumentArrowUpIcon className="w-4 h-4" />}
                    >
                      {entry.label}
                    </Button>
                  ))}
              </div>
            )}
            {canApprove && !closed && (
              <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 dark:border-gray-700 pt-4">
                {requestStatus !== 'pending_documents' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => requestDocumentsMutation.mutate()}
                    isLoading={requestDocumentsMutation.isPending}
                  >
                    Ask customer for documents
                  </Button>
                )}
                {requestStatus === 'pending_documents' && (
                  <Button
                    size="sm"
                    onClick={() => approveDocumentsMutation.mutate('in_progress')}
                    isLoading={approveDocumentsMutation.isPending}
                    disabled={outstanding.length > 0}
                    title={
                      outstanding.length > 0
                        ? 'Approve every required document first'
                        : 'Move this request on'
                    }
                  >
                    Approve documents &amp; continue
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </Card>

      <Card>
        <CardHeader
          title={`Attached Documents (${documents.length})`}
          action={
            uploadAllowed ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => openUploadFor()}
                leftIcon={<DocumentArrowUpIcon className="w-4 h-4" />}
              >
                Upload
              </Button>
            ) : undefined
          }
        />
        {documents.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
            No documents attached to this request.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4 font-medium">Document Name</th>
                  <th className="py-2 pr-4 font-medium">Type</th>
                  <th className="py-2 pr-4 font-medium">Uploaded</th>
                  <th className="py-2 pr-4 font-medium">Uploaded By</th>
                  <th className="py-2 pr-4 font-medium">Review</th>
                  <th className="py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {documents.map((doc) => (
                  <tr key={doc.id} className="align-top">
                    <td className="py-3 pr-4">
                      <p className="font-medium text-gray-900 dark:text-white break-all">{doc.name}</p>
                      {formatFileSize(doc.file_size) && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatFileSize(doc.file_size)}
                        </p>
                      )}
                      {doc.review_reason && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                          {doc.review_reason}
                        </p>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">
                      {servicingDocumentLabel(doc.document_type)}
                    </td>
                    <td className="py-3 pr-4 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {formatDateTime(doc.created_at)}
                    </td>
                    <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">
                      {doc.uploaded_by_name || '—'}
                    </td>
                    <td className="py-3 pr-4">
                      <Badge
                        variant={
                          doc.review_status === 'approved'
                            ? 'success'
                            : doc.review_status === 'rejected'
                              ? 'error'
                              : 'warning'
                        }
                        size="sm"
                      >
                        {doc.review_status === 'approved'
                          ? 'Approved'
                          : doc.review_status === 'rejected'
                            ? 'Rejected'
                            : 'Awaiting review'}
                      </Badge>
                      {doc.reviewed_by_name && (
                        <p className="text-xs text-gray-400 mt-1">by {doc.reviewed_by_name}</p>
                      )}
                    </td>
                    <td className="py-3">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadMutation.mutate(doc)}
                          isLoading={
                            downloadMutation.isPending && downloadMutation.variables?.id === doc.id
                          }
                          leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
                        >
                          Download
                        </Button>
                        {canApprove && doc.review_status !== 'approved' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600 hover:text-green-700"
                            onClick={() =>
                              reviewMutation.mutate({ document: doc, decision: 'approved', reason: '' })
                            }
                            isLoading={
                              reviewMutation.isPending &&
                              reviewMutation.variables?.document.id === doc.id &&
                              reviewMutation.variables?.decision === 'approved'
                            }
                          >
                            Approve
                          </Button>
                        )}
                        {canApprove && doc.review_status !== 'rejected' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-amber-600 hover:text-amber-700"
                            onClick={() => {
                              setRejecting(doc);
                              setRejectReason('');
                            }}
                          >
                            Reject
                          </Button>
                        )}
                        {uploadAllowed && doc.review_status !== 'approved' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 dark:text-red-400"
                            onClick={() => {
                              if (window.confirm(`Delete document "${doc.name}"?`)) {
                                deleteMutation.mutate(doc);
                              }
                            }}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal isOpen={showUpload} onClose={closeUpload} title="Upload Document">
        <div className="space-y-4">
          {/* `name` is what `Select`/`Input` use as the element id, and the id is
              what ties the <label> to the control -- without it the field has a
              visible label and no accessible name. */}
          <Select
            name="servicing_document_type"
            label="Document Type"
            options={[{ value: '', label: 'Select the document type' }, ...SERVICING_DOCUMENT_TYPES]}
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
          />
          <div>
            <label
              htmlFor="servicing_document_files"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
            >
              Files
            </label>
            <input
              id="servicing_document_files"
              type="file"
              multiple
              accept={UPLOAD_ACCEPT_ATTRIBUTE}
              onChange={handleFilesChosen}
              className="block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              PDF, JPG, PNG, WEBP, HEIC, TIFF, Word or Excel. Up to 10 MB each.
            </p>
          </div>
          {files.length === 1 && (
            <Input
              name="servicing_document_name"
              label="Name (optional)"
              placeholder={files[0].name}
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
            />
          )}
          {fileErrors.length > 0 && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-3 space-y-1">
              {fileErrors.map((message) => (
                <p key={message} className="text-sm text-red-700 dark:text-red-300">
                  {message}
                </p>
              ))}
            </div>
          )}
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={closeUpload}>
            Cancel
          </Button>
          <Button
            onClick={() => uploadMutation.mutate()}
            isLoading={uploadMutation.isPending}
            disabled={uploadMutation.isPending || files.length === 0 || !documentType}
          >
            {files.length > 1 ? `Upload ${files.length} Documents` : 'Upload'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={!!rejecting}
        onClose={() => {
          setRejecting(null);
          setRejectReason('');
        }}
        title="Reject Document"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">{rejecting?.name}</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              rows={3}
              maxLength={500}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="What is wrong with this document? The customer sees this."
            />
          </div>
        </div>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setRejecting(null);
              setRejectReason('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              rejecting &&
              reviewMutation.mutate({
                document: rejecting,
                decision: 'rejected',
                reason: rejectReason.trim(),
              })
            }
            isLoading={reviewMutation.isPending}
            disabled={!rejectReason.trim()}
          >
            Reject Document
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Pending Payment
// ---------------------------------------------------------------------------

interface PaymentCardProps extends WorkflowCardProps {
  /** `null` means the API did not report a payment record — see MissingFromApi. */
  payment: ServicingPayment | null;
  /** The customer may record what they paid. They may never verify it. */
  canRecordPayment: boolean;
  /**
   * W3h: `request_payment` now runs the documents gate server-side
   * (`service_request_workflow.documents_blocking_reason`) -- once documents
   * have been requested, billing is refused while any of them is still
   * outstanding. `undefined`/`null` means the caller has not wired this
   * prop (or the API did not report the checklist) -- treated as "unknown",
   * so the button stays enabled and the server's own refusal (with its
   * toast) is still the real gate.
   */
  checklist?: RequiredDocument[] | null;
  /**
   * W3h review: whether this request was ever put into the documents step
   * (see `readDocumentsRequested`). Required for the gate to engage because
   * the detail API ALWAYS sends `required_documents` -- with no pinned
   * override that is the request type's default list, every entry 'missing',
   * even on a request nobody asked for paperwork. The server gates
   * `request_payment` only once documents were requested, so gating on the
   * checklist alone would block billing the API allows. Anything but `true`
   * leaves the button enabled; the server's 400 (and its toast) stays the gate.
   */
  documentsRequested?: boolean;
}

/**
 * Client mirror of `service_request_workflow.documents_were_requested`: the
 * request is waiting on documents now, a Broker Admin pinned a checklist into
 * `request_data.required_documents` (even an empty one), or its audit trail
 * shows it entering or leaving `pending_documents` or a `documents_requested`
 * event. Reads an untyped detail payload; anything it cannot read is `false`.
 */
export function readDocumentsRequested(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false;
  const request = payload as { status?: unknown; request_data?: unknown; activities?: unknown };
  if (request.status === 'pending_documents') return true;
  if (
    request.request_data &&
    typeof request.request_data === 'object' &&
    !Array.isArray(request.request_data) &&
    'required_documents' in request.request_data
  ) {
    return true;
  }
  if (!Array.isArray(request.activities)) return false;
  return request.activities.some((activity) => {
    if (!activity || typeof activity !== 'object') return false;
    const entry = activity as { old_status?: unknown; new_status?: unknown; activity_data?: unknown };
    if (entry.old_status === 'pending_documents' || entry.new_status === 'pending_documents') return true;
    const data = entry.activity_data;
    return !!data && typeof data === 'object' && (data as { event?: unknown }).event === 'documents_requested';
  });
}

function PaymentField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <div className="text-sm font-medium text-gray-900 dark:text-white">{value || '—'}</div>
    </div>
  );
}

export function ServicingPaymentCard({
  requestId,
  requestStatus,
  canApprove,
  canRecordPayment,
  onChanged,
  payment,
  checklist,
  documentsRequested,
}: PaymentCardProps) {
  const closed = CLOSED_STATUSES.includes(requestStatus);
  // Outstanding = requested but not yet approved. `checklist` absent/null
  // means "unknown" (see the prop doc above), never "nothing outstanding".
  // The checklist only counts once documents were actually requested: before
  // that it is the request type's default list, which the server does not gate on.
  const outstandingDocuments =
    documentsRequested === true && checklist ? checklist.filter((entry) => entry.status !== 'approved') : [];
  const documentsBlockRequestPayment = outstandingDocuments.length > 0;
  const requestPaymentBlockedReason = documentsBlockRequestPayment
    ? `Approve every outstanding document first (${outstandingDocuments.length} pending) — the request cannot be billed while paperwork is outstanding.`
    : undefined;

  const [showRequest, setShowRequest] = useState(false);
  const [amount, setAmount] = useState('');
  const [requestNote, setRequestNote] = useState('');

  const [showRecord, setShowRecord] = useState(false);
  const [outcome, setOutcome] = useState<'initiated' | 'success' | 'failed'>('success');
  const [reference, setReference] = useState('');
  const [method, setMethod] = useState('');
  const [failureReason, setFailureReason] = useState('');

  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const requestPaymentMutation = useMutation({
    mutationFn: () =>
      serviceRequestWorkflowApi.requestPayment(requestId, {
        amount: amount.trim(),
        note: requestNote.trim(),
      }),
    onSuccess: () => {
      onChanged();
      setShowRequest(false);
      setAmount('');
      setRequestNote('');
      toast.success('Payment requested.');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const recordPaymentMutation = useMutation({
    mutationFn: () =>
      serviceRequestWorkflowApi.recordPayment(requestId, {
        outcome,
        reference: reference.trim(),
        method,
        failure_reason: failureReason.trim(),
      }),
    onSuccess: () => {
      onChanged();
      setShowRecord(false);
      setReference('');
      setFailureReason('');
      toast.success(
        outcome === 'success'
          ? 'Payment recorded. A Broker Admin will verify it.'
          : 'Payment update recorded.'
      );
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const verifyMutation = useMutation({
    mutationFn: (input: { decision: 'approved' | 'rejected'; reason?: string }) =>
      serviceRequestWorkflowApi.verifyPayment(requestId, input),
    onSuccess: (_data, variables) => {
      onChanged();
      setShowReject(false);
      setRejectReason('');
      toast.success(
        variables.decision === 'approved'
          ? 'Payment verified. The request is now with the insurer.'
          : 'Payment rejected. The request stays at Pending Payment.'
      );
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  if (payment === null) {
    return (
      <Card>
        <CardHeader title="Payment" />
        <MissingFromApi what="the payment record" />
      </Card>
    );
  }

  const noPaymentYet = payment.status === 'not_requested';
  const awaitingCustomer = ['due', 'initiated', 'failed', 'rejected'].includes(payment.status);
  const awaitingVerification = payment.status === 'paid';

  return (
    <>
      <Card>
        <CardHeader
          title="Payment"
          subtitle={paymentStatusLabel(payment.status)}
          action={
            canApprove && !closed && (noPaymentYet || payment.status === 'verified') ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowRequest(true)}
                leftIcon={<BanknotesIcon className="w-4 h-4" />}
                disabled={documentsBlockRequestPayment}
                title={requestPaymentBlockedReason}
              >
                Request Payment
              </Button>
            ) : undefined
          }
        />

        {noPaymentYet ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No payment has been requested on this request.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <PaymentField
                label="Amount Due"
                value={payment.amount ? `${payment.currency} ${payment.amount}` : null}
              />
              <PaymentField
                label="Status"
                value={
                  <Badge variant={paymentBadgeVariant(payment.status)} size="sm">
                    {paymentStatusLabel(payment.status)}
                  </Badge>
                }
              />
              <PaymentField label="Transaction Reference" value={payment.reference} />
              <PaymentField label="Method" value={payment.method} />
              <PaymentField
                label="Requested"
                value={
                  payment.requested_at
                    ? `${formatDateTime(payment.requested_at)}${
                        payment.requested_by_name ? ` by ${payment.requested_by_name}` : ''
                      }`
                    : null
                }
              />
              <PaymentField
                label="Paid"
                value={payment.paid_at ? formatDateTime(payment.paid_at) : null}
              />
              {payment.status === 'verified' && (
                <PaymentField
                  label="Verified"
                  value={
                    payment.verified_at
                      ? `${formatDateTime(payment.verified_at)}${
                          payment.verified_by_name ? ` by ${payment.verified_by_name}` : ''
                        }`
                      : null
                  }
                />
              )}
            </div>

            {payment.failure_reason && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                Last failure: {payment.failure_reason}
              </p>
            )}
            {payment.rejection_reason && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                Rejected by the Broker Admin: {payment.rejection_reason}
              </p>
            )}

            {awaitingVerification && !canApprove && (
              <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
                This payment is with a Broker Admin for verification. The request moves to
                &ldquo;With Insurer&rdquo; only once they have approved it.
              </p>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {canRecordPayment && !closed && awaitingCustomer && (
                <Button size="sm" onClick={() => setShowRecord(true)}>
                  Record a payment
                </Button>
              )}
              {canApprove && !closed && awaitingVerification && (
                <>
                  <Button
                    size="sm"
                    onClick={() => verifyMutation.mutate({ decision: 'approved' })}
                    isLoading={verifyMutation.isPending}
                  >
                    Verify &amp; approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowReject(true)}>
                    Reject payment
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </Card>

      <Modal isOpen={showRequest} onClose={() => setShowRequest(false)} title="Request Payment">
        <div className="space-y-4">
          {documentsBlockRequestPayment && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-800 dark:text-amber-200">{requestPaymentBlockedReason}</p>
            </div>
          )}
          <Input
            name="servicing_payment_amount"
            label="Amount Due (INR)"
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="1500.00"
          />
          <Input
            name="servicing_payment_note"
            label="Note (optional)"
            value={requestNote}
            onChange={(e) => setRequestNote(e.target.value)}
            placeholder="What is this charge for?"
          />
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowRequest(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => requestPaymentMutation.mutate()}
            isLoading={requestPaymentMutation.isPending}
            disabled={documentsBlockRequestPayment || !amount.trim() || Number(amount) <= 0}
            title={requestPaymentBlockedReason}
          >
            Request Payment
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showRecord} onClose={() => setShowRecord(false)} title="Record a payment">
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Recording a payment does not approve it. A Broker Admin checks the transaction against
            their records before the request moves on.
          </p>
          <Select
            name="servicing_payment_outcome"
            label="What happened?"
            options={PAYMENT_OUTCOMES.map((o) => ({ value: o.value, label: o.label }))}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value as 'initiated' | 'success' | 'failed')}
          />
          {outcome === 'success' && (
            <>
              <Input
                name="servicing_payment_reference"
                label="Transaction Reference"
                required
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="UPI / bank reference number"
              />
              <Select
                name="servicing_payment_method"
                label="Method"
                options={[{ value: '', label: 'Select a method' }, ...PAYMENT_METHODS]}
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              />
            </>
          )}
          {outcome === 'failed' && (
            <Input
              name="servicing_payment_failure_reason"
              label="What went wrong? (optional)"
              value={failureReason}
              onChange={(e) => setFailureReason(e.target.value)}
              placeholder="Card declined"
            />
          )}
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowRecord(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => recordPaymentMutation.mutate()}
            isLoading={recordPaymentMutation.isPending}
            disabled={outcome === 'success' && !reference.trim()}
          >
            Record
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showReject} onClose={() => setShowReject(false)} title="Reject Payment">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              rows={3}
              maxLength={500}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Why was the payment not accepted? The customer sees this."
            />
          </div>
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowReject(false)}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              verifyMutation.mutate({ decision: 'rejected', reason: rejectReason.trim() })
            }
            isLoading={verifyMutation.isPending}
            disabled={!rejectReason.trim()}
          >
            Reject Payment
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
